import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Users, Calendar, MessageSquare, Bell, ChevronRight, Clock, MapPin, 
  RefreshCw, ClipboardList, Dumbbell, Video, Target, Trophy, Zap, Star
} from 'lucide-react';
import { format, parseISO, isToday, isTomorrow, isThisWeek } from 'date-fns';
import ManagerDashboard from './ManagerDashboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeamDashboard = () => {
  const ctx = useOutletContext();
  const { user, currentTeam, isManager } = ctx;

  // Team managers get a dedicated operational command center instead of the
  // performance-oriented coach dashboard.
  if (isManager) {
    return <ManagerDashboard />;
  }

  return <CoachDashboard {...ctx} />;
};

const CoachDashboard = ({ user, currentTeam, isManager }) => {
  const [stats, setStats] = useState(null);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (currentTeam) {
      fetchDashboardData();
    }
  }, [currentTeam]);

  const fetchDashboardData = async (showRefreshing = false) => {
    const token = localStorage.getItem('team_token');
    if (!token) return;
    
    if (showRefreshing) setRefreshing(true);

    try {
      // Fetch workspace stats
      const workspaceRes = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/workspace`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (workspaceRes.ok) {
        const data = await workspaceRes.json();
        setStats(data.stats);
      }

      // Fetch upcoming sessions
      const sessionsRes = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/sessions?status=scheduled`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (sessionsRes.ok) {
        const sessions = await sessionsRes.json();
        // Sort by date and take first 5
        const upcoming = sessions
          .filter(s => new Date(s.date) >= new Date())
          .slice(0, 5);
        setUpcomingSessions(upcoming);
      }

      // Fetch recent messages
      const messagesRes = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/messages?limit=3`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (messagesRes.ok) {
        const messages = await messagesRes.json();
        setRecentMessages(messages);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  const getSessionDateLabel = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    if (isThisWeek(date)) return format(date, 'EEEE');
    return format(date, 'MMM d');
  };

  const getSessionTypeColor = (type) => {
    switch (type) {
      case 'game': return 'bg-red-500/10 text-red-400 border-red-500/30';
      case 'tournament': return 'bg-purple-500/10 text-purple-400 border-purple-500/30';
      case 'meeting': return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
      default: return 'bg-green-500/10 text-green-400 border-green-500/30';
    }
  };

  // Coach level indicator
  const getTeamLevel = () => {
    const athleteCount = stats?.athletes || 0;
    if (athleteCount >= 20) return { level: 'Elite Team', icon: Trophy, color: 'text-yellow-400', bg: 'bg-yellow-500/10' };
    if (athleteCount >= 10) return { level: 'Growing Team', icon: Star, color: 'text-blue-400', bg: 'bg-blue-500/10' };
    return { level: 'Starter Team', icon: Zap, color: 'text-muted-foreground', bg: 'bg-muted/30' };
  };

  const teamLevel = getTeamLevel();

  // Quick Actions - Different for Coach vs Manager
  const coachQuickActions = [
    { label: 'New Session', icon: Calendar, link: `/team/${currentTeam?.team_id}/sessions?action=new`, color: 'bg-green-500/10 hover:bg-green-500/20 text-green-400' },
    { label: 'Training Plan', icon: ClipboardList, link: `/team/${currentTeam?.team_id}/training-plans`, color: 'bg-orange-500/10 hover:bg-orange-500/20 text-orange-400' },
    { label: 'View Athletes', icon: Users, link: `/team/${currentTeam?.team_id}/athletes`, color: 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-400' },
    { label: 'Send Message', icon: MessageSquare, link: `/team/${currentTeam?.team_id}/messages?action=new`, color: 'bg-purple-500/10 hover:bg-purple-500/20 text-purple-400' },
  ];

  const managerQuickActions = [
    { label: 'New Session', icon: Calendar, link: `/team/${currentTeam?.team_id}/sessions?action=new`, color: 'bg-green-500/10 hover:bg-green-500/20 text-green-400' },
    { label: 'View Athletes', icon: Users, link: `/team/${currentTeam?.team_id}/athletes`, color: 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-400' },
    { label: 'Send Message', icon: MessageSquare, link: `/team/${currentTeam?.team_id}/messages?action=new`, color: 'bg-purple-500/10 hover:bg-purple-500/20 text-purple-400' },
    { label: 'Notifications', icon: Bell, link: `/team/${currentTeam?.team_id}/notifications`, color: 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-400' },
  ];

  const quickActions = isManager ? managerQuickActions : coachQuickActions;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 overflow-x-hidden" data-testid="team-dashboard">
      {/* Header with Team Level */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <h1 className="font-heading text-xl sm:text-2xl md:text-3xl uppercase tracking-tight truncate">Dashboard</h1>
            <p className="text-muted-foreground text-xs sm:text-sm md:text-base truncate">
              Welcome back, {user?.full_name?.split(' ')[0]} • {currentTeam?.team_name}
            </p>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-muted-foreground hover:text-primary flex-shrink-0"
            title="Refresh data"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          </Button>
        </div>
        <div className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-sm self-start md:self-auto flex-shrink-0 ${teamLevel.bg}`}>
          <teamLevel.icon className={teamLevel.color} size={16} />
          <span className={`text-xs sm:text-sm font-medium ${teamLevel.color}`}>{teamLevel.level}</span>
        </div>
      </div>

      {/* Role Badge */}
      <div className={`inline-flex items-center px-3 py-1.5 rounded text-xs uppercase font-semibold ${
        isManager 
          ? 'bg-blue-500/10 text-blue-400 border border-blue-500/30' 
          : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
      }`}>
        {isManager ? 'Team Manager' : 'Team Coach'}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
        {quickActions.map((action, idx) => (
          <Link 
            key={idx} 
            to={action.link}
            className={`flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-sm border border-border/40 transition-colors min-h-[48px] ${action.color}`}
          >
            <action.icon size={18} />
            <span className="font-medium text-xs md:text-sm">{action.label}</span>
          </Link>
        ))}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-card border border-border/40 p-3 sm:p-4 md:p-5 rounded-sm">
          <div className="flex items-center justify-between mb-2 md:mb-3">
            <Users className="text-blue-400" size={18} />
          </div>
          <p className="font-heading text-xl sm:text-2xl md:text-3xl mb-1">{stats?.athletes || 0}</p>
          <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Athletes</p>
        </div>
        
        <div className="bg-card border border-border/40 p-3 sm:p-4 md:p-5 rounded-sm">
          <div className="flex items-center justify-between mb-2 md:mb-3">
            <Calendar className="text-green-400" size={18} />
          </div>
          <p className="font-heading text-xl sm:text-2xl md:text-3xl mb-1">{stats?.sessions || 0}</p>
          <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Sessions</p>
        </div>
        
        {!isManager && (
          <>
            <div className="bg-card border border-border/40 p-3 sm:p-4 md:p-5 rounded-sm">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <ClipboardList className="text-orange-400" size={18} />
              </div>
              <p className="font-heading text-xl sm:text-2xl md:text-3xl mb-1">{stats?.training_plans || 0}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Training Plans</p>
            </div>
            
            <div className="bg-card border border-border/40 p-3 sm:p-4 md:p-5 rounded-sm">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <Dumbbell className="text-purple-400" size={18} />
              </div>
              <p className="font-heading text-xl sm:text-2xl md:text-3xl mb-1">{stats?.drills || 0}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Drills</p>
            </div>
          </>
        )}
        
        {isManager && (
          <>
            <div className="bg-card border border-border/40 p-3 sm:p-4 md:p-5 rounded-sm">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <MessageSquare className="text-blue-400" size={18} />
              </div>
              <p className="font-heading text-xl sm:text-2xl md:text-3xl mb-1">{recentMessages.length}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Messages</p>
            </div>
            
            <div className="bg-card border border-border/40 p-3 sm:p-4 md:p-5 rounded-sm">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <Bell className="text-amber-400" size={18} />
              </div>
              <p className="font-heading text-xl sm:text-2xl md:text-3xl mb-1">{upcomingSessions.length}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Upcoming</p>
            </div>
          </>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Upcoming Sessions */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Upcoming Sessions</CardTitle>
              <Link to={`/team/${currentTeam.team_id}/sessions`}>
                <Button variant="ghost" size="sm">
                  View All <ChevronRight size={16} />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {upcomingSessions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No upcoming sessions scheduled
              </p>
            ) : (
              upcomingSessions.map((session) => (
                <div 
                  key={session.id}
                  className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                  data-testid={`session-${session.id}`}
                >
                  <div className={`px-2 py-1 rounded text-xs font-medium border ${getSessionTypeColor(session.session_type)}`}>
                    {session.session_type.charAt(0).toUpperCase() + session.session_type.slice(1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{session.title}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {getSessionDateLabel(session.date)} • {format(parseISO(session.date), 'HH:mm')}
                      </span>
                      {session.location && (
                        <span className="flex items-center gap-1 truncate">
                          <MapPin size={12} />
                          {session.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}

            <Link to={`/team/${currentTeam.team_id}/sessions`}>
              <Button variant="outline" className="w-full mt-2">
                <Calendar size={16} className="mr-2" />
                Manage Sessions
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Messages */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Team Messages</CardTitle>
              <Link to={`/team/${currentTeam.team_id}/messages`}>
                <Button variant="ghost" size="sm">
                  View All <ChevronRight size={16} />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentMessages.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No messages yet
              </p>
            ) : (
              recentMessages.map((message) => (
                <div 
                  key={message.id}
                  className="p-3 bg-muted/50 rounded-lg"
                  data-testid={`message-${message.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-sm">{message.title}</p>
                    <span className="text-xs text-muted-foreground">
                      {format(parseISO(message.created_at), 'MMM d')}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {message.message}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    By {message.sent_by_name}
                  </p>
                </div>
              ))
            )}

            <Link to={`/team/${currentTeam.team_id}/messages`}>
              <Button variant="outline" className="w-full mt-2">
                <MessageSquare size={16} className="mr-2" />
                Send Message
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Coach-only: Training Resources */}
      {!isManager && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Training Resources</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Link to={`/team/${currentTeam.team_id}/training-plans`}>
                <Button variant="outline" className="w-full h-auto py-4 flex-col">
                  <ClipboardList size={24} className="mb-2 text-orange-400" />
                  <span className="text-xs">Training Plans</span>
                </Button>
              </Link>
              <Link to={`/team/${currentTeam.team_id}/drills`}>
                <Button variant="outline" className="w-full h-auto py-4 flex-col">
                  <Dumbbell size={24} className="mb-2 text-purple-400" />
                  <span className="text-xs">Drill Library</span>
                </Button>
              </Link>
              <Link to={`/team/${currentTeam.team_id}/videos`}>
                <Button variant="outline" className="w-full h-auto py-4 flex-col">
                  <Video size={24} className="mb-2 text-blue-400" />
                  <span className="text-xs">Videos</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TeamDashboard;
